/* Babygami cart actions: quick add, cart-drawer add-ons, and the "upgrade" swaps.
   Uses Horizon's own cart event (CartLinesUpdateEvent) so the drawer re-renders through the
   Section Rendering API and auto-opens on add, exactly like Horizon's product form. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const routes = () => (window.Theme && window.Theme.routes) || {};
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return [...new Set(ids)];
}

async function getCart() {
  const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}

/**
 * Runs one cart request and tells Horizon about it.
 * @param {HTMLElement} source - element that started the change (the event is dispatched from it)
 * @param {'add'|'update'} action
 * @param {{id:number, quantity:number}[]} lines - lines for the event (merchandise + quantity)
 * @param {() => Promise<any>} request - performs the fetch and returns the JSON response
 */
async function run(source, action, lines, request) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'product',
      lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    const response = await request();
    const cart = await getCart();
    if (response && response.status) {
      source.dispatchEvent(new CartErrorEvent({ error: response.message || 'Cart update failed', code: 'INVALID', detail: { description: response.description } }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'bg-cart' } });
      return false;
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'bg-cart', sections: response && response.sections, didError: false, itemCount: lines.reduce((n, l) => n + l.quantity, 0) },
    });
    return true;
  } catch (err) {
    deferred.reject(err);
    return false;
  }
}

/** Add one or more variants. */
export function addVariants(source, items) {
  return run(source, 'add', items, () =>
    fetch(routes().cart_add_url || `${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: cartSectionIds().join(',') }),
    }).then((r) => r.json())
  );
}

/** Replace one variant with another in a single request (used by the upgrade offers). */
export function swapVariant(source, fromId, toId, quantity) {
  return run(source, 'update', [{ id: toId, quantity }], async () => {
    const cart = await getCart();
    const already = cart.items.filter((i) => i.variant_id === Number(toId)).reduce((n, i) => n + i.quantity, 0);
    return fetch(`${(routes().cart_update_url || `${root()}cart/update`)}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: { [fromId]: 0, [toId]: already + quantity }, sections: cartSectionIds().join(',') }),
    }).then((r) => r.json());
  });
}

/* Click handling for any [data-bg-add] / [data-bg-swap] button on the page, including inside the drawer
   (which is re-rendered after each change, so listen on the document). */
document.addEventListener('click', async (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-bg-add],[data-bg-swap-from]') : null;
  if (!btn || btn.getAttribute('aria-busy') === 'true') return;
  e.preventDefault();
  btn.setAttribute('aria-busy', 'true');
  try {
    if (btn.hasAttribute('data-bg-swap-from')) {
      await swapVariant(btn, btn.dataset.bgSwapFrom, btn.dataset.bgSwapTo, Number(btn.dataset.bgQuantity || 1));
    } else {
      await addVariants(btn, [{ id: Number(btn.dataset.bgAdd), quantity: Number(btn.dataset.bgQuantity || 1) }]);
    }
  } finally {
    btn.removeAttribute('aria-busy');
  }
});

window.BabygamiCart = { addVariants, swapVariant };
